import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Company } from "../api/models/companies.models";

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  margin-left: 1rem;
  flex-grow: 1;
  text-align: left;
`;

const FilterToggle = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #333;
  font-weight: 500;
  max-width: fit-content;
  padding: 0.5rem 1rem;
  background-color: var(--sumi-blue);
  color: var(--sumi-text-primary);
  border-radius: var(--border-radius);
  margin-bottom: 1rem;

  &:hover {
    background-color: var(--sumi-darker-blue);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--sumi-darker-blue);
  }
`;

const ChevronIcon = styled.i<{ isOpen: boolean }>`
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: #d3d3d3 1px solid;
`;

interface AdminUserFiltersProps {
  onFiltersChange?: (filters: UserFilters) => void;
  companies?: Company[];
  filters: UserFilters;
  debouncedFilters: UserFilters;
  setFilters: React.Dispatch<React.SetStateAction<UserFilters>>;
  setDebouncedFilters: React.Dispatch<React.SetStateAction<UserFilters>>;
}

export interface UserFilters {
  name?: string;
  email?: string;
  companyId?: number;
  companyCode?: string;
}

const AdminUserFilters: React.FC<AdminUserFiltersProps> = ({
  onFiltersChange,
  companies,
  filters,
  debouncedFilters,
  setFilters,
  setDebouncedFilters,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const initializedRef = React.useRef(false);

  useEffect(() => {
    if (
      !initializedRef.current &&
      filters.companyId &&
      companies &&
      companies.length > 0
    ) {
      const company = companies.find((c) => c.id === filters.companyId);
      if (company) {
        setSelectedCompany(company);
        initializedRef.current = true;
      }
    }
  }, [filters.companyId, companies]);

  const handleFilterChange = (
    key: keyof UserFilters,
    value: string | number,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleCompanyChange = (company: Company | null) => {
    setSelectedCompany(company);
    setFilters((prev) => ({
      ...prev,
      companyId: company?.id || undefined,
    }));
  };

  const businessIdentifierId = 50;

  const searchCompanies = (event: { query: string }) => {
    const query = event.query.toLowerCase();
    let filtered =
      companies?.filter((company) => {
        const businessId = company.identifiers
          .find((id) => id.identifierType.id === businessIdentifierId)
          ?.text?.toLowerCase();
        return (
          company.companyName.toLowerCase().includes(query) ||
          businessId?.includes(query)
        );
      }) || [];

    // Sort by company name
    filtered = filtered.sort((a, b) =>
      a.companyName.localeCompare(b.companyName, undefined, {
        sensitivity: "base",
      }),
    );

    setFilteredCompanies(filtered);
  };

  return (
    <FilterContainer>
      <FilterToggle
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
      >
        <span>{t("label-filters")}</span>
        <ChevronIcon className="pi pi-chevron-down" isOpen={isOpen} />
      </FilterToggle>

      {isOpen && (
        <FiltersGrid>
          <div>
            <label
              htmlFor="name"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("label-name")}
            </label>
            <InputText
              id="name"
              value={filters.name || ""}
              onChange={(e) => handleFilterChange("name", e.target.value)}
              placeholder={t("placeholder-search-name")}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label
              htmlFor="email"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("label-email")}
            </label>
            <InputText
              id="email"
              value={filters.email || ""}
              onChange={(e) => handleFilterChange("email", e.target.value)}
              placeholder={t("placeholder-search-email")}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label
              htmlFor="company"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("label-company-name-or-business-id")}
            </label>
            <AutoComplete
              id="company"
              value={selectedCompany}
              suggestions={filteredCompanies}
              completeMethod={searchCompanies}
              field="companyName"
              onChange={(e) => handleCompanyChange(e.value)}
              placeholder={t("placeholder-search-company-name")}
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
              dropdown
              itemTemplate={(company: Company) => {
                const businessId = company.identifiers.find(
                  (id) => id.identifierType.id === businessIdentifierId,
                )?.text;
                return businessId
                  ? `${company.companyName} - ${businessId}`
                  : company.companyName;
              }}
              selectedItemTemplate={(company: Company) => {
                const businessId = company.identifiers.find(
                  (id) => id.identifierType.id === businessIdentifierId,
                )?.text;
                return businessId
                  ? `${company.companyName} - ${businessId}`
                  : company.companyName;
              }}
            />
          </div>
        </FiltersGrid>
      )}
    </FilterContainer>
  );
};

export default AdminUserFilters;
